import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { forkJoin, of, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AlertService } from "../services/alert.service";
import { EnvService } from "../services/env.service";
import { MatTableDataSource } from "@angular/material/table";
import { AuthService } from "../services/auth.service";

@Component({
  selector: "micro-cmdb-hardware-request-detail",
  templateUrl: "./cmdb-hardware-request-detail.component.html",
})
export class CmdbHardwareRequestDetailComponent implements OnInit, OnDestroy {
  dataSource = new MatTableDataSource<any>();
  @Input() hardwareRequestId: any;
  @Output() persisted = new EventEmitter<any>();
  hardwareRequest: any;
  requestNumber: number = 0;

  selectedValues: any = {};
  validatingCompany = false;
  companyValidationFailed = false;
  private companyValidation?: Subscription;
  private loadRequest?: Subscription;
  private subscriptions = new Subscription();
  private readonly companyFields = [
    { id: 'applicationId', selected: 'application', name: 'applicationName', endpoint: '/domain/cmdbApplications' },
    { id: 'classCodeId', selected: 'classCodeId', name: 'classCodeName', endpoint: '/domain/cmdbClassCodes' },
    { id: 'serverTypeId', selected: 'serverTypeId', name: 'serverTypeName', endpoint: '/domain/cmdbServerTypes' },
    { id: 'osTypeId', selected: 'osTypeId', name: 'osTypeName', endpoint: '/domain/cmdbOsType' },
    { id: 'projectManagerContactId', selected: 'projectManagerContactId', name: 'projectManagerContactName', endpoint: '/auth/contacts' }
  ];

  get hasRequiredSelections(): boolean {
    return !!this.hardwareRequest?.companyId
      && this.companyFields.every(field => !!this.hardwareRequest[field.id]);
  }

  constructor(
    public auth: AuthService,
    private env: EnvService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    if (this.hardwareRequestId) {
      this.load(this.hardwareRequestId);
    } else {
      this.subscriptions.add(this.route.params.subscribe((params: Params) => {
        params["id"] === "0" ? this.createNew() : this.load(params["id"]);
      }));
    }
  }

  load(id: string): void {
    this.loadRequest?.unsubscribe();
    this.companyValidation?.unsubscribe();
    this.selectedValues = {};
    this.hardwareRequest = undefined;
    this.companyValidationFailed = false;
    this.loadRequest = this.http
      .get(`${this.env.e.url}/domain/cmdbHardwareRequests/${id}`)
      .subscribe((data) => {
        this.hardwareRequest = data;
        this.validateCompanySelections();
      });
  }

  createNew(): void {
    this.loadRequest?.unsubscribe();
    this.companyValidation?.unsubscribe();
    this.selectedValues = {};
    this.validatingCompany = false;
    this.companyValidationFailed = false;
    this.hardwareRequest = {
      requestNumber: this.requestNumber,
      requestedBy: this.auth.getUserId(),
      companyId: this.auth.getCompanyId(),
    };
  }

  save(): void {
    if (this.validatingCompany || this.companyValidationFailed || !this.hasRequiredSelections) {
      return;
    }
    const params = new HttpParams().set('companyId', this.hardwareRequest.companyId);
    const isUpdate = !!this.hardwareRequest.id;
    const request = isUpdate
      ? this.http.put(
          `${this.env.e.url}/domain/cmdbHardwareRequests/${this.hardwareRequest.id}`,
          this.hardwareRequest,
          { params },
        )
      : this.http.post(
          `${this.env.e.url}/domain/cmdbHardwareRequests`,
          this.hardwareRequest,
          { params },
        );
    this.subscriptions.add(request.subscribe((data) => {
      this.hardwareRequest = data;
      this.validateCompanySelections();
      this.alertService.info(
        `${isUpdate ? "Updated" : "Created"} ${this.hardwareRequest.serverName} request`,
      );
      this.persisted.emit({ data, action: isUpdate ? "updated" : "added" });
    }));
  }

  delete(): void {
    this.http
      .delete(`${this.env.e.url}/domain/cmdbHardwareRequests/${this.hardwareRequest.id}`)
      .subscribe(() => {
        this.persisted.emit({ data: this.hardwareRequest, action: "deleted" });
        this.alertService.info(`Deleted ${this.hardwareRequest.name}`);
        if (!this.hardwareRequestId) {
          this.router.navigate(["/hardwareRequests"]);
        }
      });
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.hardwareRequest.active = target.checked ? "Y" : "N";
  }

  onSelectedChange(key: string, value: any): void {
    this.selectedValues[key] = value;
    const field = this.companyFields.find(field => field.selected === key);
    if (field) {
      this.hardwareRequest[field.name] = value?.name || null;
    }
  }

  change(event: Event) {
    const target = event.target as HTMLInputElement;
    this.hardwareRequest.active = target.checked ? "Y" : "N";
  }

  onApplicationSelectedChange(application: any): void {
    this.onSelectedChange('application', application);
    this.hardwareRequest.applicationTagId = application?.applicationTagId || null;
    this.hardwareRequest.applicationTagName = application?.applicationTagName || null;
    this.validateCompanySelections();
  }

  onCompanyChange(companyId: any): void {
    if (companyId === this.hardwareRequest.companyId) return;
    this.hardwareRequest.companyId = companyId;
    this.validateCompanySelections();
  }

  validateCompanySelections(): void {
    this.companyValidation?.unsubscribe();
    this.companyValidationFailed = false;
    this.validatingCompany = false;
    const request = this.hardwareRequest;
    const companyId = request.companyId;
    if (!companyId) return;

    // Validate the selected records themselves, independently of dropdown pages.
    const fields = this.companyFields.map(field => ({ ...field, value: request[field.id] }));
    const selections = fields.map(field => {
      if (!field.value) return of(null);
      const record = this.http.get<any>(`${this.env.e.url}${field.endpoint}/${field.value}`);
      if (field.id !== 'applicationId') return record;
      return record.pipe(switchMap(application => {
        if (!application?.applicationTagId || application.companyId !== companyId) return of(application);
        return this.http.get<any>(`${this.env.e.url}/domain/cmdbApplicationTags/${application.applicationTagId}`)
          .pipe(map(tag => ({ ...application, applicationTagName: tag?.name || null })));
      }));
    });

    this.validatingCompany = true;
    this.companyValidation = forkJoin(selections).subscribe({
      next: records => {
        if (this.hardwareRequest !== request || request.companyId !== companyId) return;
        let unresolved = false;
        fields.forEach((field, index) => {
          const record = records[index];
          if (field.value && !record?.companyId) unresolved = true;
          if (record?.companyId && record.companyId !== companyId) request[field.id] = null;
          const selected = request[field.id] ? record : undefined;
          this.selectedValues[field.selected] = selected;
          request[field.name] = selected?.name || null;
        });
        const application = this.selectedValues.application;
        request.applicationTagId = application?.applicationTagId || null;
        request.applicationTagName = application?.applicationTagName || null;
        this.companyValidationFailed = unresolved;
        this.validatingCompany = false;
      },
      error: () => {
        this.validatingCompany = false;
        this.companyValidationFailed = true;
        this.alertService.warn('Unable to validate the selected records for this company. Please retry.');
      }
    });
  }

  ngOnDestroy(): void {
    this.companyValidation?.unsubscribe();
    this.loadRequest?.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
